import React, { useState, useMemo, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    AppBar, Toolbar, IconButton, Typography, Box, Drawer, List, ListItemButton,
    ListItemIcon, ListItemText, Divider, useTheme, useMediaQuery, Avatar, Menu, MenuItem, Chip
} from '@mui/material';
import {
    Menu as MenuIconLucide,
    Home,
    Users,
    Building2,
    Settings,
    Router,
    Activity,
    LogOut,
    BookOpen,
    ScrollText,
    Cog,
    KeyRound,
    ShieldCheck,
    SlidersHorizontal,
    FlaskConical,
    HardDrive,
    Sun,
    Moon,
    Monitor,
    Check,
    Atom,
    Radar,
    BellRing,
} from 'lucide-react';
import { useKeycloak } from '@react-keycloak/web';
import { ThemeContext } from '../theme/AppThemeProvider';
import useResumoAlertas from '../pages/Frota/useResumoAlertas';

// Largura da sidebar (fonte única — usada só aqui; o layout em App.js reserva o
// espaço automaticamente via flexbox, sem precisar duplicar esse valor).
export const DRAWER_WIDTH = 260;
const HEADER_HEIGHT = 64;

const avatarFor = (user) => ({
    initials: user.split(' ').slice(0, 2).map((p) => p[0]?.toUpperCase()).join('') || '??',
});

export default function Nav() {
    const { keycloak } = useKeycloak();
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [mobileOpen, setMobileOpen] = useState(false);
    const [anchor, setAnchor] = useState(null);
    const [themeAnchor, setThemeAnchor] = useState(null);
    const { preference, setColorMode } = useContext(ThemeContext);

    const isActive = (path) => location.pathname === path || location.pathname.startsWith(`${path}/`);
    const ThemeIcon = preference === 'system' ? Monitor : preference === 'dark' ? Moon : Sun;
    const themeLabel = preference === 'system' ? 'Sistema' : preference === 'dark' ? 'Escuro' : 'Claro';

    const has = (r) => keycloak?.hasRealmRole?.(r);
    const isAdmin = !!has('ADMIN');
    const isGestor = !!has('GESTOR');
    // Contador de alertas da frota (só ADMIN busca).
    const resumoAlertas = useResumoAlertas(isAdmin);
    const alertasAbertos = resumoAlertas?.abertos || 0;

    const menuLinks = useMemo(() => {
        if (isAdmin) {
            return [
                { label: 'Home', icon: <Home size={20} />, path: '/home' },
                { label: 'Dispositivos', icon: <Router size={20} />, path: '/dispositivos' },
                { label: 'Frota', icon: <Radar size={20} />, path: '/frota' },
                { label: 'Alertas', icon: <BellRing size={20} />, path: '/alertas', contador: 'alertas' },
                { label: 'Sensores', icon: <Activity size={20} />, path: '/sensores' },
                { label: 'Sensor de Cor', icon: <SlidersHorizontal size={20} />, path: '/sensor-cor' },
                { label: 'Usuarios', icon: <Users size={20} />, path: '/usuarios' },
                { label: 'Clientes', icon: <Building2 size={20} />, path: '/clientes' },
                { label: 'Protocolos', icon: <BookOpen size={20} />, path: '/protocolos' },
                { label: 'Licenciamentos (Admin)', icon: <ScrollText size={20} />, path: '/admin/licenciamentos' },
                { label: 'Certificados', icon: <ShieldCheck size={20} />, path: '/certificados' },
                { label: 'Definicoes', icon: <Cog size={20} />, path: '/definicoes' },
                { label: 'Firmwares', icon: <HardDrive size={20} />, path: '/firmwares' },
            ];
        }
        if (isGestor) {
            return [
                { label: 'Home', icon: <Home size={20} />, path: '/home' },
                { label: 'Area do Cliente', icon: <KeyRound size={20} />, path: '/external' },
                { label: 'Meus Licenciamentos', icon: <ScrollText size={20} />, path: '/licenciamentos' },
                { label: 'Certificados', icon: <ShieldCheck size={20} />, path: '/certificados' },
                { label: 'Recursos', icon: <FlaskConical size={20} />, path: '/recursos' },
            ];
        }
        return [];
    }, [isAdmin, isGestor]);

    if (!keycloak?.authenticated) return null;

    const username = keycloak.tokenParsed?.preferred_username || 'Usuario';
    const avatar = avatarFor(username);
    const logout = () => keycloak.logout({ redirectUri: window.location.origin });

    const currentLabel = menuLinks.find(({ path }) => isActive(path))?.label || 'BioAILab IOT';

    const handleNavigate = (path) => {
        navigate(path);
        if (isMobile) setMobileOpen(false);
    };

    const headerBg = theme.palette.mode === 'dark' ? '#060A10' : theme.palette.primary.main;

    const drawerContent = (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box
                sx={{
                    height: HEADER_HEIGHT,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 2.5,
                    flexShrink: 0,
                    bgcolor: headerBg,
                    // A AppBar é um Paper com elevation=1, que no dark mode ganha um leve
                    // overlay branco (regra do MUI); replicamos aqui pra bater exatamente
                    // com a cor renderizada da navbar, não só com o hex "cru".
                    backgroundImage:
                        theme.palette.mode === 'dark'
                            ? 'linear-gradient(rgba(255,255,255,0.051), rgba(255,255,255,0.051))'
                            : 'none',
                    color: '#FFFFFF',
                }}
            >
                <Atom size={22} />
                <Typography variant="subtitle1" sx={{ fontWeight: 800, letterSpacing: -0.3, color: 'inherit' }}>
                    BioAILab IOT
                </Typography>
            </Box>
            <Divider />

            <Box sx={{ px: 2.5, pt: 2, pb: 0.5 }}>
                <Typography
                    variant="caption"
                    sx={{ fontWeight: 700, letterSpacing: 1, fontSize: '0.6rem', textTransform: 'uppercase', color: 'text.disabled' }}
                >
                    Navegação
                </Typography>
            </Box>

            <List sx={{ flexGrow: 1, overflowY: 'auto', px: 1 }}>
                {menuLinks.map(({ label, icon, path, contador }) => (
                    <ListItemButton key={label} selected={isActive(path)} onClick={() => handleNavigate(path)}>
                        <ListItemIcon>{icon}</ListItemIcon>
                        <ListItemText primary={label} />
                        {contador === 'alertas' && alertasAbertos > 0 && (
                            <Chip
                                size="small"
                                color={resumoAlertas?.criticos ? 'error' : 'warning'}
                                label={alertasAbertos}
                                sx={{ height: 20, fontWeight: 700 }}
                                aria-label={`${alertasAbertos} alertas abertos`}
                            />
                        )}
                    </ListItemButton>
                ))}
            </List>

            <Divider />
            <Box sx={{ p: 1, flexShrink: 0 }}>
                <ListItemButton onClick={(e) => setThemeAnchor(e.currentTarget)}>
                    <ListItemIcon><ThemeIcon size={20} /></ListItemIcon>
                    <ListItemText primary="Tema" secondary={themeLabel} />
                </ListItemButton>
                {isAdmin && (
                    <ListItemButton onClick={() => handleNavigate('/configuracoes')}>
                        <ListItemIcon><Settings size={20} /></ListItemIcon>
                        <ListItemText primary="Configuracoes" />
                    </ListItemButton>
                )}
                <ListItemButton onClick={logout} sx={{ color: 'error.main', '& .MuiListItemIcon-root': { color: 'error.main' } }}>
                    <ListItemIcon><LogOut size={20} /></ListItemIcon>
                    <ListItemText primary="Sair" />
                </ListItemButton>
            </Box>
        </Box>
    );

    return (
        <>
            <AppBar
                position="fixed"
                elevation={1}
                sx={{
                    width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
                    ml: { md: `${DRAWER_WIDTH}px` },
                }}
            >
                <Toolbar>
                    {isMobile && (
                        <IconButton color="inherit" onClick={() => setMobileOpen((v) => !v)} edge="start" sx={{ mr: 1 }}>
                            <MenuIconLucide size={22} />
                        </IconButton>
                    )}

                    <Typography variant="h6" sx={{ flexGrow: 1, color: 'inherit', fontWeight: 700 }}>
                        {currentLabel}
                    </Typography>

                    <IconButton onClick={(e) => setAnchor(e.currentTarget)} sx={{ ml: 1, p: 0.5 }}>
                        <Avatar sx={{ bgcolor: 'secondary.main', color: 'secondary.contrastText', width: 36, height: 36, fontSize: '0.875rem', fontWeight: '600' }}>
                            {avatar.initials}
                        </Avatar>
                    </IconButton>
                </Toolbar>
            </AppBar>

            <Menu
                anchorEl={themeAnchor}
                open={Boolean(themeAnchor)}
                onClose={() => setThemeAnchor(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                PaperProps={{ elevation: 2, sx: { minWidth: 180 } }}
            >
                <MenuItem selected={preference === 'light'} onClick={() => { setColorMode('light'); setThemeAnchor(null); }}>
                    <ListItemIcon><Sun size={18} /></ListItemIcon>
                    Claro
                    {preference === 'light' && (
                        <Box sx={{ ml: 'auto', display: 'flex' }}><Check size={16} /></Box>
                    )}
                </MenuItem>
                <MenuItem selected={preference === 'dark'} onClick={() => { setColorMode('dark'); setThemeAnchor(null); }}>
                    <ListItemIcon><Moon size={18} /></ListItemIcon>
                    Escuro
                    {preference === 'dark' && (
                        <Box sx={{ ml: 'auto', display: 'flex' }}><Check size={16} /></Box>
                    )}
                </MenuItem>
                <MenuItem selected={preference === 'system'} onClick={() => { setColorMode('system'); setThemeAnchor(null); }}>
                    <ListItemIcon><Monitor size={18} /></ListItemIcon>
                    Sistema
                    {preference === 'system' && (
                        <Box sx={{ ml: 'auto', display: 'flex' }}><Check size={16} /></Box>
                    )}
                </MenuItem>
            </Menu>

            <Menu
                anchorEl={anchor}
                open={Boolean(anchor)}
                onClose={() => setAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{ elevation: 2, sx: { mt: 1.5, minWidth: 180 } }}
            >
                {isAdmin && (
                    <MenuItem onClick={() => { navigate('/configuracoes'); setAnchor(null); }}>
                        <ListItemIcon><Settings size={18} /></ListItemIcon>
                        Configuracoes
                    </MenuItem>
                )}
                <MenuItem onClick={() => { logout(); setAnchor(null); }}>
                    <ListItemIcon sx={{ color: 'error.main' }}><LogOut size={18} /></ListItemIcon>
                    Sair
                </MenuItem>
            </Menu>

            <Box component="nav" sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}>
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={() => setMobileOpen(false)}
                    ModalProps={{ keepMounted: true }}
                    sx={{
                        display: { xs: 'block', md: 'none' },
                        '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' },
                    }}
                >
                    {drawerContent}
                </Drawer>
                <Drawer
                    variant="permanent"
                    open
                    sx={{
                        display: { xs: 'none', md: 'block' },
                        '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' },
                    }}
                >
                    {drawerContent}
                </Drawer>
            </Box>
        </>
    );
}
